/**
 * Hook: useStepSubmit
 *
 * Generic step submit handler that replaces per-page onSubmit logic.
 *
 * On submit:
 * 1. Sync final form data to stores via field bindings
 * 2. Evaluate API contracts — fire any newly satisfiable APIs
 * 3. Evaluate branching rules for navigation overrides
 * 4. Mark step as submitted + navigate
 */
import { useCallback } from "react";
import { useAlert } from "@chhjit/react-components";

import { useFieldBindings } from "./use-field-bindings";
import { usePreSubmitValidation } from "./pre-submit-validation";
import { useApiOrchestrator } from "./use-api-orchestrator";
import { evaluateBranchingRules } from "./branching-rules";
import { readStorePath } from "./store-accessors";
import type { ContractExecutors } from "./api-contracts";
import { useServiceSteps, wizardApi } from "../../../entities/wizard";
import { useCustomNavigate } from "../../hooks";
import { bookingProcessApi } from "../../../entities/booking-process";
import { routePaths } from "../../configs";

type UseStepSubmitOptions = {
  /** API contract executors — wired to existing hooks */
  executors: ContractExecutors;
  /** Optional callback before API evaluation */
  beforeApiEvaluation?: (formData: Record<string, unknown>) => Promise<void>;
  /** Optional callback after successful submit */
  afterSubmit?: () => void;
};

export const useStepSubmit = (
  stepId: string,
  options: UseStepSubmitOptions,
) => {
  const { showAlert } = useAlert();
  const { navigate } = useCustomNavigate();
  const { syncToStores } = useFieldBindings(stepId);
  const { runAll: runPreSubmitValidators } = usePreSubmitValidation();
  const { evaluateContracts } = useApiOrchestrator(options.executors);
  const { category, categorySteps, current, nextRoute } = useServiceSteps();

  const onSubmit = useCallback(
    async (formData: Record<string, unknown>) => {
      if (!category?.slug) {
        showAlert("Please select a service to continue", {
          variant: "error",
          autoHideDuration: null,
        });
        return;
      }

      // 0. Run field-level pre-submit validators (e.g. promo-code). A blocking
      // validator has already surfaced its own inline message (orange "not
      // valid" text) — keep the user on the step without a generic alert.
      const preSubmitValid = await runPreSubmitValidators();
      if (!preSubmitValid) {
        return;
      }

      let spinnerTimeout: ReturnType<typeof setTimeout> | undefined;
      try {
        // 1. Sync form data to stores
        syncToStores(formData);

        // 2. Optional pre-evaluation hook
        if (options.beforeApiEvaluation) {
          await options.beforeApiEvaluation(formData);
        }

        // 3. Evaluate API contracts
        // Delay the truck spinner by 1s — if the step completes faster than
        // that the user never sees a loading flash. For slow API calls (e.g.
        // appointmentCreate on congested networks) the spinner kicks in after 1s.
        spinnerTimeout = setTimeout(() => {
          bookingProcessApi.setIsBooking(true);
        }, 1000);

        const result = await evaluateContracts();
        clearTimeout(spinnerTimeout);

        // If any critical contract errored, don't navigate.
        // Individual executors that fire alerts (e.g. fetchAvailability for
        // unavailable time slots) have already informed the user. For silent
        // failures (missing prerequisites, unexpected API errors that don't
        // surface their own alert), show the generic error here.
        if (result.errors.length > 0) {
          const firstErrorEntry = result.errors[0];
          const firstError = firstErrorEntry.error as any;

          // serviceCheck already navigated to the not-serviced page — skip the
          // generic alert to avoid showing a redundant red banner.
          const isServiceCheckError =
            firstErrorEntry.contractId === "serviceCheck";

          // fetchAvailability returns false and throws a plain Error when the
          // slot is unavailable — it already showed an alert. Skip the generic
          // alert in that case to avoid showing two alerts back-to-back.
          const isAvailabilityError =
            firstError instanceof Error &&
            firstError.message === "Selected time slot is no longer available";

          if (isAvailabilityError) {
            // fetchAvailability already showed the "slot no longer available"
            // alert. Navigate back to calendar-windows so the user can pick a
            // new time — they have no way to do that from truck-loads.
            const calendarStep = categorySteps?.find(([key]) =>
              key.includes("calendar-windows"),
            );
            if (calendarStep) {
              navigate(`${calendarStep[0]}?category=${category?.slug}`);
            }
          } else if (!isServiceCheckError) {
            const series = firstError?.response?.data?.meta?.series;
            const seriesPrefix = series ? `Series ${series}: ` : "";
            showAlert(
              seriesPrefix +
                "An unexpected error has occurred. Please contact us at (888) 689-5999 to complete your appointment.",
              {
                variant: "error",
                autoHideDuration: null,
              },
            );
          }
          // Restore booking state so user can retry
          bookingProcessApi.setIsBooking(false);
          return;
        }

        // 4. Mark step submitted (uses step path, not stepId)
        wizardApi.setSubmitted({
          service: category.slug,
          step: current?.key,
          submitted: true,
        });

        // 5. Evaluate branching rules
        let branchOverride = evaluateBranchingRules({
          stepId,
          categorySlug: category.slug,
        });

        // callRequestConfirmed hidden field: only redirect to confirmation if
        // callRequest actually fired (prerequisites were met + API succeeded).
        // Checking result.fired rather than the store value prevents premature
        // redirect when the field is present but account.id isn't ready yet.
        if (
          !branchOverride &&
          result.fired.includes("callRequest") &&
          readStorePath("appointment", "callRequestConfirmed")
        ) {
          branchOverride =
            routePaths.confirmation + "?category=" + category.slug;
        }

        // Check if any terminal contracts fired.
        // - appointmentCreate: Junk booking completed
        // - quoteCreate: Moving booking completed (fires at additional-factors via bookingConfirmed)
        // - callRequest: Labor / business dwelling / call booking type
        const isTerminal =
          result.fired.includes("appointmentCreate") ||
          result.fired.includes("quoteCreate") ||
          result.fired.includes("callRequest");

        if (isTerminal) {
          // Ensure isBooking is true before setting isBooked so the PageLayout
          // guard (isBooked && !isBooking) doesn't redirect before navigation.
          bookingProcessApi.setIsBooking(true);
          bookingProcessApi.setIsBooked(true);
        }

        // 6. Navigate
        navigate(branchOverride || nextRoute);

        // 7. Optional post-submit hook
        options.afterSubmit?.();
      } catch (error: any) {
        // Restore booking state so user can retry
        clearTimeout(spinnerTimeout);
        bookingProcessApi.setIsBooking(false);

        const series = error?.response?.data?.meta?.series;
        const seriesPrefix = series ? `Series ${series}: ` : "";

        showAlert(
          seriesPrefix +
            "An unexpected error has occurred. Please contact us at (888) 689-5999 to complete your appointment.",
          {
            variant: "error",
            autoHideDuration: null,
          },
        );
      }
    },
    [
      category?.slug,
      categorySteps,
      current?.key,
      evaluateContracts,
      navigate,
      nextRoute,
      options,
      runPreSubmitValidators,
      showAlert,
      stepId,
      syncToStores,
    ],
  );

  return onSubmit;
};
